/**
 * useAutoSave - CR-5461
 *
 * Debounced server-side auto-save. Watches a serialisable value and calls
 * the provided save function after `delayMs` of inactivity. Returns a
 * status indicator the UI can surface to reassure the user their work is
 * persisted.
 *
 * Designed for the RFQ wizard but generic enough to reuse for any future
 * server-backed draft flow.
 *
 * @param value         The state to persist. Changes trigger the timer.
 * @param save          Async function that persists `value` server-side.
 * @param delayMs       Debounce delay in ms (default 3000).
 * @param enabled       When false, skips all saves. Useful while showing a
 *                      "resume draft?" prompt or before the user has
 *                      meaningfully interacted with the form.
 * @returns { status, lastSavedAt } — status is one of:
 *          'idle'   - no save attempted yet this session
 *          'saving' - in-flight save
 *          'saved'  - last save succeeded
 *          'error'  - last save failed
 */
import { useEffect, useRef, useState } from 'react';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function useAutoSave<T>(
  value: T,
  save: (value: T) => Promise<void>,
  delayMs: number = 3000,
  enabled: boolean = true,
): { status: AutoSaveStatus; lastSavedAt: Date | null } {
  const [status, setStatus] = useState<AutoSaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!enabled) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setStatus('saving');
      try {
        await saveRef.current(valueRef.current);
        setStatus('saved');
        setLastSavedAt(new Date());
      } catch {
        // Network/server error — surface as 'error' but don't toast on
        // every tick (auto-save failure shouldn't spam the user). The
        // UI shows a status pill the user can hover to retry.
        setStatus('error');
      }
    }, delayMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // value is intentionally the only dep that triggers re-run. save/delayMs/enabled
    // changes via refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, enabled]);

  return { status, lastSavedAt };
}
